import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cpFetch, createAuthHeaders } from '../../lib/cpClient';
import { buildEventSourcePath } from '../../lib/eventSourceAuth';
import { useAuthStore } from '../../store/authStore';

export type ObserveConnectionType = 'mcp' | 'external_api';
export type ObserveStatus = 'active' | 'stale' | 'down' | 'never';
export type StatsWindow = '1h' | '6h' | '24h' | '7d';
export type UptimeWindow = '24h' | '7d' | '30d';

export type ObserveConnection = {
  connection_id: string;
  type: ObserveConnectionType;
  label: string;
  app_id: string | null;
  last_seen: string | null;
  status: ObserveStatus;
  total_calls_24h: number;
  error_rate_24h: number | null;
  direction: string | null;
  calls_per_hour: Array<{ hour: string; count: number }>;
};

export type ObserveStreamEvent = {
  id: string;
  time: string;
  type: ObserveConnectionType | 'mcp' | 'external_api';
  connection_id: string;
  label: string;
  app_id: string | null;
  success: boolean | null;
  latency_ms: number | null;
  error_message: string | null;
};

export type ConnectionStats = {
  connection_id: string;
  call_count: number;
  success_count: number;
  error_count: number;
  error_rate_pct: number | null;
  p50_latency_ms: number | null;
  p95_latency_ms: number | null;
  avg_latency_ms: number | null;
  active_since: string | null;
  calls_per_hour: Array<{ hour: string; count: number; error_count?: number }>;
  top_errors: Array<{ error_message: string; count: number }>;
};

export type UptimeSegment = { start: string; end: string; status: 'up' | 'down' };

export type UptimePayload = {
  uptime_pct: number;
  total_up_minutes: number;
  total_down_minutes: number;
  segments: UptimeSegment[];
  longest_outage: { start: string; end: string; duration_minutes: number } | null;
  last_incident: { start: string; end: string; duration_minutes: number } | null;
  has_data: boolean;
};

function useAuthHeaders() {
  const { token, selectedTenant } = useAuthStore();
  return token && selectedTenant ? createAuthHeaders(token, selectedTenant) : null;
}

export function useConnections(appIdFilter?: string) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['api-observe', 'connections', appIdFilter || ''],
    enabled: Boolean(headers),
    refetchInterval: 30_000,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (appIdFilter) params.set('app_id', appIdFilter);
      const qs = params.toString();
      return cpFetch(`/services/api-observe/connections${qs ? `?${qs}` : ''}`, { headers: headers! });
    },
  });
}

export function useConnectionStats(connectionId: string | null, window: StatsWindow) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['api-observe', 'stats', connectionId, window],
    enabled: Boolean(headers && connectionId),
    refetchInterval: 60_000,
    queryFn: async () => {
      const params = new URLSearchParams({ window, connection_id: connectionId! });
      const resp = await cpFetch(`/services/api-observe/stats?${params}`, { headers: headers! });
      const row = (resp?.stats || []).find((s: ConnectionStats) => s.connection_id === connectionId) || resp?.stats?.[0];
      return { has_data: Boolean(resp?.has_data && row), stats: row as ConnectionStats | undefined, raw: resp };
    },
  });
}

export function useUptimeTimeline(connectionId: string | null, window: UptimeWindow) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ['api-observe', 'uptime', connectionId, window],
    enabled: Boolean(headers && connectionId),
    refetchInterval: 5 * 60_000,
    queryFn: async () => {
      const params = new URLSearchParams({ window });
      const resp = await cpFetch(
        `/services/api-observe/uptime/${encodeURIComponent(connectionId!)}?${params}`,
        { headers: headers! }
      );
      return resp as UptimePayload & { ok: boolean };
    },
  });
}

export type StreamStatus = 'connected' | 'reconnecting' | 'disconnected';

const MAX_EVENTS = 200;

export function useEventStream(enabled = true) {
  const [events, setEvents] = useState<ObserveStreamEvent[]>([]);
  const [status, setStatus] = useState<StreamStatus>('disconnected');
  const esRef = useRef<EventSource | null>(null);
  const backoffRef = useRef(1000);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const append = useCallback((ev: ObserveStreamEvent) => {
    setEvents((prev) => {
      const next = [...prev, ev];
      return next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('disconnected');
      return undefined;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      const url = buildEventSourcePath('/cp/services/api-observe/events/stream', {
        since: new Date(Date.now() - 60_000).toISOString(),
      });
      if (!url) {
        setStatus('disconnected');
        return;
      }
      setStatus('reconnecting');
      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        backoffRef.current = 1000;
        setStatus('connected');
      };

      es.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data) as ObserveStreamEvent;
          append(data);
        } catch {
          // ignore malformed
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        if (cancelled) return;
        setStatus('reconnecting');
        const delay = backoffRef.current;
        backoffRef.current = Math.min(backoffRef.current * 2, 8000);
        timerRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      setStatus('disconnected');
    };
  }, [enabled, append]);

  return { events, status, clearEvents: () => setEvents([]) };
}
